import { useMemo } from 'react';
import { format, subDays, addDays } from 'date-fns';
import { useInterviews, type NeuraxInterview } from '@/lib/interviews/useInterviews';
import { useLicenses, type NeuraxLicense } from '@/lib/licenses/useLicenses';
import { useEndClients, type NeuraxEndClient } from '@/lib/end-clients/useEndClients';
import { useEmployees, type NeuraxEmployee } from '@/lib/employees/useEmployees';

// ── Types ──────────────────────────────────────────────

export interface SessionDayBucket {
  date: string;
  interviews: number;
  assessments: number;
}

export interface SessionTypeSlice {
  type: 'interview' | 'online_assessment';
  label: string;
  count: number;
  color: string;
}

export interface ActivityEvent {
  id: string;
  type:
    | 'interview_scheduled'
    | 'interview_completed'
    | 'license_activated'
    | 'candidate_added'
    | 'employee_joined';
  title: string;
  description: string;
  timestamp: string;
  accent: 'electric' | 'plasma' | 'amber' | 'mint';
}

export interface DashboardAnalytics {
  sessionsOverTime: SessionDayBucket[];
  sessionTypeBreakdown: SessionTypeSlice[];
  activityFeed: ActivityEvent[];
  isLoading: boolean;
}

// ── Helpers ────────────────────────────────────────────

function buildSessionBuckets(sessions: NeuraxInterview[]): SessionDayBucket[] {
  const now = new Date();
  const buckets: Record<string, SessionDayBucket> = {};

  // 14-day window centered on today — 6 days back through 7 days forward —
  // so the chart reflects both recent history and upcoming scheduled sessions.
  for (let offset = -6; offset <= 7; offset++) {
    const day = offset >= 0 ? addDays(now, offset) : subDays(now, -offset);
    const key = format(day, 'yyyy-MM-dd');
    buckets[key] = { date: format(day, 'MMM d'), interviews: 0, assessments: 0 };
  }

  for (const s of sessions) {
    const dateStr = s.interview_date || s.created_at;
    const d = new Date(dateStr);
    const key = format(d, 'yyyy-MM-dd');
    const bucket = buckets[key];
    if (!bucket) continue;
    if (s.session_type === 'online_assessment') {
      bucket.assessments++;
    } else {
      bucket.interviews++;
    }
  }

  return Object.values(buckets);
}

function buildTypeBreakdown(sessions: NeuraxInterview[]): SessionTypeSlice[] {
  let interviews = 0;
  let assessments = 0;
  for (const s of sessions) {
    if (s.session_type === 'online_assessment') assessments++;
    else interviews++;
  }
  return [
    { type: 'interview', label: 'Interviews', count: interviews, color: '#5B7CFF' },
    { type: 'online_assessment', label: 'Assessments', count: assessments, color: '#8B5CF6' },
  ];
}

function fmtDuration(s: number | null) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m} min` : `${s}s`;
}

function buildActivityFeed(
  sessions: NeuraxInterview[],
  licenses: NeuraxLicense[],
  endClients: NeuraxEndClient[],
  employees: NeuraxEmployee[]
): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  const cutoff = subDays(new Date(), 30);

  // Sessions → scheduled or completed
  for (const s of sessions) {
    const name = s.end_client?.name || 'Unknown candidate';
    const role = s.position_title || 'Untitled role';
    const company = s.company_name || '';

    if (s.status === 'completed' || s.status === 'ended') {
      const durStr = fmtDuration(s.duration_seconds);
      const msgStr = s.message_count ? `${s.message_count} messages` : '';
      const details = [name, role, company, durStr, msgStr].filter(Boolean).join(' · ');
      events.push({
        id: `session-done-${s.id}`,
        type: 'interview_completed',
        title:
          s.session_type === 'online_assessment' ? 'Assessment completed' : 'Interview completed',
        description: details,
        timestamp: s.ended_at || s.created_at,
        accent: 'mint',
      });
    } else {
      const details = [name, role, company].filter(Boolean).join(' · ');
      events.push({
        id: `session-sched-${s.id}`,
        type: 'interview_scheduled',
        title:
          s.session_type === 'online_assessment' ? 'Assessment scheduled' : 'Interview scheduled',
        description: details,
        timestamp: s.created_at,
        accent: 'electric',
      });
    }
  }

  // Licenses → activated (has machine_id or activated_at)
  for (const l of licenses) {
    if (!l.machine_id && l.status !== 'active') continue;
    const name = l.end_client?.name || 'Unknown candidate';
    const keyPreview =
      l.license_key.length > 12
        ? `${l.license_key.slice(0, 4)}...${l.license_key.slice(-4)}`
        : l.license_key;
    events.push({
      id: `lic-${l.id}`,
      type: 'license_activated',
      title: 'License activated',
      description: `${name} activated ${keyPreview}${l.machine_id ? ' · device bound' : ''}`,
      timestamp: l.last_used_at || l.created_at,
      accent: 'amber',
    });
  }

  // End clients → added
  for (const c of endClients) {
    const created = new Date(c.created_at);
    if (created < cutoff) continue;
    const details = [c.name, c.job_title, c.target_company ? `targeting ${c.target_company}` : '']
      .filter(Boolean)
      .join(' · ');
    events.push({
      id: `cand-${c.id}`,
      type: 'candidate_added',
      title: 'New candidate added',
      description: details,
      timestamp: c.created_at,
      accent: 'plasma',
    });
  }

  // Employees → joined
  for (const e of employees) {
    const created = new Date(e.created_at);
    if (created < cutoff) continue;
    const name = e.full_name || e.email;
    events.push({
      id: `emp-${e.id}`,
      type: 'employee_joined',
      title: `${name} joined the team`,
      description: `Signed in as ${e.role}`,
      timestamp: e.created_at,
      accent: 'electric',
    });
  }

  // Sort by timestamp descending, limit to 20
  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events.slice(0, 20);
}

// ── Hook ───────────────────────────────────────────────

export function useDashboardAnalytics(): DashboardAnalytics {
  const { data: sessions, isLoading: loadingSessions } = useInterviews('all');
  const { data: licenses, isLoading: loadingLicenses } = useLicenses();
  const { data: endClients, isLoading: loadingClients } = useEndClients();
  const { data: employees, isLoading: loadingEmployees } = useEmployees();

  const isLoading = loadingSessions || loadingLicenses || loadingClients || loadingEmployees;

  return useMemo(() => {
    const s = sessions ?? [];
    const l = licenses ?? [];
    const c = endClients ?? [];
    const e = employees ?? [];

    return {
      sessionsOverTime: buildSessionBuckets(s),
      sessionTypeBreakdown: buildTypeBreakdown(s),
      activityFeed: buildActivityFeed(s, l, c, e),
      isLoading,
    };
  }, [sessions, licenses, endClients, employees, isLoading]);
}
